import { apiClient } from "@/services/api"
import { defineStore } from "pinia"
import { hasPermission, hasPermissionsOfAtLeastOne } from "@/services/permissionService"
import { PERMISSIONS } from "@/services/permission"
import { hasMoreRows } from "@/services/listAnswer"
import { DEFAULT_IDLE_DAYS, DEFAULT_RENTAL_TERMS } from "@/services/machinePurchaseUnits"
import { tariffsRequest } from "@/services/transportTariffs"

let lastPage = -1
// The supplier the page asked for last (`loadSupplier`).
let requestedSupplierId = null

export const SUPPLIER_FORM_NAMES = {
  GENERAL: "general",
  CONTACTS: "contacts",
  LOCATIONS: "locations",
  ASSORTMENT: "assortment",
  FILES: "files",
  ACTIVITY: "activity",
}

/** "Eigener Betrieb": the workspace's own stock, one per workspace, never deleted (docs/suppliers.md). */
export const SUPPLIER_BUILT_IN = {
  OWN_COMPANY: "OWN_COMPANY",
}

/**
 * A supplier by its name alone, as a picker's "als neuen Lieferanten anlegen" creates it (`POST /supplier`); kind,
 * address and conditions are added on its page. The overview's and the detail page's state are left alone.
 */
export const createSupplierNamed = name =>
  apiClient.post("/supplier", { name }, { headers: { tenant: true, authorization: true } }).then(res => res.data)

/** "Eigener Betrieb"'s id: the same in every workspace (the server's `SupplierIds.OWN_COMPANY`). */
export const OWN_COMPANY_SUPPLIER_ID = "supplier-own-company"

/**
 * Who reads a supplier's conditions (Rabatt, Skonto, Fracht): a reader of either catalogue's purchase prices, as
 * the server answers them (docs/suppliers.md). Changing them also takes the supplier's edit right.
 */
export const canViewSupplierConditions = () =>
  hasPermissionsOfAtLeastOne([PERMISSIONS.VIEW_MACHINE_PURCHASEPRICE, PERMISSIONS.VIEW_MATERIAL_PURCHASEPRICE]).value
export const canEditSupplierConditions = () =>
  hasPermission(PERMISSIONS.EDIT_SUPPLIER_GENERAL).value &&
  hasPermissionsOfAtLeastOne([PERMISSIONS.EDIT_MACHINE_PURCHASEPRICE, PERMISSIONS.EDIT_MATERIAL_PURCHASEPRICE]).value
/**
 * Miete and Transport (`rentalTerms`, `transportTariffs`) are a machine's: the server answers them to a reader of the
 * machines' purchase prices and takes them with the machines' purchase price edit right (and the supplier's).
 */
export const canViewRentalTerms = () => hasPermission(PERMISSIONS.VIEW_MACHINE_PURCHASEPRICE).value
export const canEditRentalTerms = () =>
  hasPermission(PERMISSIONS.EDIT_SUPPLIER_GENERAL).value && hasPermission(PERMISSIONS.EDIT_MACHINE_PURCHASEPRICE).value

export const blankSupplierConditions = () => ({
  discounts: [],
  cashDiscount: null,
  freight: null,
  rentalTerms: null,
  transportTariffs: [],
})

const _blankSupplier = () => {
  return {
    id: "",
    name: "",
    // The kind's id on the way out; the server answers `{ id, name }`, which `kindIdOf` reads.
    kind: null,
    address: null,
    contacts: null,
    // Further addresses of the supplier, each with its own contacts (docs/suppliers.md, "Standorte").
    locations: [],
    customerNumber: null,
    telephoneNumber: null,
    email: null,
    website: null,
    note: null,
    files: null,
    // Null where the server would answer null: the general tab shows the section only for a value.
    conditions: canEditSupplierConditions() ? blankSupplierConditions() : null,
    builtIn: null,
    createdAt: null,
    createdBy: null,
    lastModifiedAt: null,
    lastModifiedBy: null,
  }
}

/** The kind a supplier carries, as an id: the response names it `{ id, name }`, the request sends the id. */
export const kindIdOf = supplier => {
  const kind = supplier?.kind
  return kind && typeof kind === "object" ? kind.id : (kind ?? null)
}

const amountOrNull = value => (value == null || value === "" || Number.isNaN(Number(value)) ? null : Number(value))
const amounts = map => {
  const kept = Object.fromEntries(
    Object.entries(map ?? {})
      .map(([currency, value]) => [currency, amountOrNull(value)])
      .filter(([, value]) => value != null)
  )
  return Object.keys(kept).length ? kept : null
}

/**
 * The conditions as the server takes them: numbers, and an empty Skonto or freight as none. Null keeps what is
 * stored, which is what a reader without the right sends back.
 */
const conditionsRequest = conditions => {
  if (!conditions) return null
  const cashPercent = amountOrNull(conditions.cashDiscount?.percent)
  const perDelivery = amounts(conditions.freight?.perDelivery)
  const freeFrom = amounts(conditions.freight?.freeFrom)
  const note = conditions.freight?.note?.trim() || null
  return {
    discounts: (conditions.discounts ?? []).map(({ validFrom, percent }) => ({
      validFrom,
      percent: amountOrNull(percent),
    })),
    cashDiscount:
      cashPercent == null ? null : { percent: cashPercent, days: amountOrNull(conditions.cashDiscount.days) },
    freight: perDelivery || freeFrom || note ? { perDelivery, freeFrom, note } : null,
    // Only from whoever may change them (`canEditRentalTerms`), else null: the stored ones stay. Without the machines'
    // purchase price right a reader gets none, and the defaults sent for them would overwrite the supplier's own.
    rentalTerms: canEditRentalTerms() ? rentalTermsRequest(conditions.rentalTerms) : null,
    // Null for conditions that came without them, too.
    transportTariffs: canEditRentalTerms() ? tariffsRequest(conditions.transportTariffs) : null,
  }
}

// Rental days per week and month (docs/suppliers.md, "Einheit je Preiszeile"), always sent: the server keeps the
// stored terms for a request without them (a tab of before sends none), so an emptied field goes back to its default
// by sending it (5 and 20 are stored as none). The same for what a day without usage costs ("Tage ohne Einsatz"):
// all three percentages or none, the defaults (100, 0, 0) stored as none.
const rentalTermsRequest = terms => ({
  daysPerWeek: amountOrNull(terms?.daysPerWeek) ?? DEFAULT_RENTAL_TERMS.daysPerWeek,
  daysPerMonth: amountOrNull(terms?.daysPerMonth) ?? DEFAULT_RENTAL_TERMS.daysPerMonth,
  idleDays: Object.fromEntries(
    Object.entries(DEFAULT_IDLE_DAYS).map(([key, fallback]) => [key, amountOrNull(terms?.idleDays?.[key]) ?? fallback])
  ),
})

const textOrNull = value => (typeof value === "string" ? value.trim() || null : (value ?? null))

/**
 * A location as the server takes it: a new one without an id (the server gives it one), blank texts as none, an
 * address without a single field as none.
 */
const locationRequest = location => {
  const address = location.address
  const hasAddress = address && Object.values(address).some(value => value != null && value !== "")
  return {
    ...(location.id ? { id: location.id } : {}),
    name: textOrNull(location.name),
    address: hasAddress ? address : null,
    telephoneNumber: textOrNull(location.telephoneNumber),
    email: textOrNull(location.email),
    note: textOrNull(location.note),
    contacts: location.contacts ?? [],
  }
}

/**
 * What the create and update requests send: the kind as its id, the conditions tidied, the locations as above (null,
 * where a form never had them, keeps the stored ones), the rest as edited.
 */
const toRequest = supplier => ({
  ...supplier,
  kind: kindIdOf(supplier),
  conditions: conditionsRequest(supplier.conditions),
  locations: supplier.locations == null ? null : supplier.locations.map(locationRequest),
})

export const useSupplierStore = defineStore("supplier", {
  state: () => ({
    suppliers: [],
    supplier: _blankSupplier(),
  }),
  getters: {
    getSuppliers: state => state.suppliers,
    getSupplier: state => state.supplier,
  },
  actions: {
    setSuppliers(suppliers) {
      this.suppliers = [...suppliers]
    },
    addSuppliers(suppliers) {
      suppliers.forEach(supplier => this.suppliers.push(supplier))
    },
    clearSuppliers() {
      this.suppliers = []
    },
    setSupplier(supplier) {
      this.supplier = supplier
    },
    clearSupplier() {
      requestedSupplierId = null
      this.supplier = _blankSupplier()
    },
    /**
     * The page's supplier. Only the one asked last is taken: a slow answer for the supplier left behind must not
     * replace the one opened since (its form, and the next save, would be the other's).
     */
    loadSupplier(id) {
      if (!hasPermission(PERMISSIONS.VIEW_SUPPLIER_GENERAL).value) return Promise.resolve(null)

      requestedSupplierId = String(id)
      return apiClient.get(`/supplier/${id}`, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 200 && requestedSupplierId === String(id)) {
          this.setSupplier(res.data)
        }
        return res
      })
    },
    /** One supplier, returned rather than put into the store (the store holds the page's own). Silent. */
    fetchSupplier(id) {
      if (!hasPermission(PERMISSIONS.VIEW_SUPPLIER_GENERAL).value) return Promise.resolve(null)
      return apiClient
        .get(`/supplier/${id}`, { headers: { tenant: true, authorization: true }, silent: true })
        .then(res => res.data)
    },
    loadSuppliers({ page, search, sortBy, sortDesc, selected, itemsPerPage, referenceType, referenceId, archive }) {
      if (!hasPermission(PERMISSIONS.VIEW_SUPPLIER_GENERAL).value) return Promise.resolve(null)

      return apiClient
        .get("/supplier", {
          params: {
            page,
            search,
            sortBy,
            sortDesc,
            selected,
            itemsPerPage,
            referenceType,
            referenceId,
            // `ARCHIVED` for the overview's archive; undefined lists the suppliers in use (docs/archiving.md).
            archive,
          },
          headers: { tenant: true, authorization: true },
        })
        .then(res => {
          if (res.status === 200) {
            if (page === 0 || lastPage === page || lastPage !== page - 1) {
              this.setSuppliers(res.data.content)
            } else {
              this.addSuppliers(res.data.content)
            }
            lastPage = page
          }
          return res
        })
    },
    /**
     * A page of suppliers for a picker, returned rather than written into the store: the store's list
     * is the overview's rows. Silent, as every picker's search is.
     */
    searchSuppliers({ search, page = 0, itemsPerPage }) {
      if (!hasPermission(PERMISSIONS.VIEW_SUPPLIER_GENERAL).value) return Promise.resolve({ items: [], hasMore: false })

      return apiClient
        .get("/supplier", {
          params: { page, itemsPerPage, search: search || undefined },
          headers: { tenant: true, authorization: true },
          silent: true,
        })
        .then(res => ({ items: res.data?.content ?? [], hasMore: hasMoreRows(res.data, itemsPerPage) }))
    },
    createSupplier(supplier, files = []) {
      if (!hasPermission(PERMISSIONS.EDIT_SUPPLIER_GENERAL).value) return Promise.resolve(null)

      const request = toRequest(supplier)
      if (files && files.length > 0) {
        const formData = new FormData()
        formData.append("supplier", new Blob([JSON.stringify(request)], { type: "application/json" }))
        files.forEach(file => formData.append("files", file))
        return apiClient
          .post("/supplier/with-files", formData, { headers: { tenant: true, authorization: true } })
          .then(res => {
            if (res.status === 201) this.setSupplier(res.data)
            return res
          })
      }
      return apiClient.post("/supplier", request, { headers: { tenant: true, authorization: true } }).then(res => {
        if (res.status === 201) this.setSupplier(res.data)
        return res
      })
    },
    updateSupplier(supplier) {
      // Null rather than a request, which `markSavedWhen` reads as "not saved".
      if (!hasPermission(PERMISSIONS.EDIT_SUPPLIER_GENERAL).value) return null

      return apiClient
        .put(`/supplier/${supplier.id}`, toRequest(supplier), { headers: { tenant: true, authorization: true } })
        .then(res => {
          // Not over another supplier opened while it was saved.
          if (res.status === 200 && this.supplier.id === res.data?.id) this.setSupplier(res.data)
          return res
        })
    },
  },
})
